import { Link, useMatchRoute } from "@tanstack/react-router";

import { useAuth } from "../../auth/AuthContext";
import { useBrand } from "../../brand/BrandContext";
import murrayAvatar from "../../assets/murray-avatar-badge-180.png";
import { cn } from "../../lib/utils";

type SidebarItem = {
  key: string;
  label: string;
  mobileLabel?: string;
  href: string;
  external?: boolean;
  icon: SidebarIconName;
};

type SidebarIconName =
  | "today"
  | "review"
  | "brain"
  | "speed"
  | "quotes"
  | "schedule"
  | "sms"
  | "reviews"
  | "import"
  | "admin";

const primaryItems: SidebarItem[] = [
  { key: "today", label: "Today", icon: "today", href: "/" },
  { key: "dailyReview", label: "Daily Review", mobileLabel: "Review", icon: "review", href: "/daily-review" },
  { key: "murrayLab", label: "Murray Lab", mobileLabel: "Brain", icon: "brain", href: "/murray-lab" },
  { key: "speedToLead", label: "Speed to Lead", mobileLabel: "Speed", icon: "speed", href: "/speed-to-lead" },
  { key: "quotes", label: "Quotes", icon: "quotes", href: "/quotes" },
  { key: "import", label: "Import data", mobileLabel: "Import", icon: "import", href: "/ops" },
  { key: "schedule", label: "Schedule", mobileLabel: "Plan", icon: "schedule", href: "/schedule" },
  { key: "sms", label: "SMS", icon: "sms", href: "/sms" },
  { key: "reviews", label: "Reviews", icon: "reviews", href: "/reviews" },
];

const adminItem: SidebarItem = { key: "admin", label: "Admin", icon: "admin", href: "/settings" };

export function Sidebar() {
  const { hasFeature, hasRole } = useAuth();
  const { brandName } = useBrand();
  const locationLabel = brandName ? brandName.replace(/^Sir Grout\s+/i, "") : "Location";
  const visiblePrimaryItems = primaryItems.filter((item) => {
    if (hasRole("sales")) return ["today", "schedule", "quotes", "sms", "import"].includes(item.key);
    if (item.key === "speedToLead") return hasRole("owner") && hasFeature("speed_to_lead");
    if (item.key === "dailyReview" || item.key === "murrayLab") return hasRole("owner", "gm");
    if (item.key === "import") return hasRole("owner", "gm", "sales");
    if (item.key === "sms") return hasRole("owner", "gm", "sales");
    return true;
  });
  const showAdmin = hasRole("owner", "gm");

  return (
    <aside className="hidden shrink-0 bg-[#080b36] bg-[radial-gradient(circle_at_top_left,rgba(36,130,203,0.24),transparent_35%),linear-gradient(180deg,#0b0d42_0%,#070a2f_100%)] px-5 py-7 text-white md:block md:min-h-[calc(100vh-64px)] md:w-[212px]">
      <div className="mb-8 flex flex-col items-center text-center">
        <img
          alt="Murray"
          className="h-20 w-20 rounded-full border-4 border-white bg-white object-cover shadow-[0_10px_28px_rgba(0,0,0,0.28)]"
          src={murrayAvatar}
        />
        <div className="mt-4 text-2xl font-semibold tracking-normal text-white">Murray</div>
        <div className="mt-1 text-sm text-slate-300">AI operating advisor</div>
        <div className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold text-emerald-300">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" aria-hidden="true" />
          {locationLabel}
        </div>
      </div>

      <nav className="space-y-2">
        {visiblePrimaryItems.map((item) => (
          <SidebarLink key={item.key} item={item} />
        ))}

        {showAdmin ? <SidebarLink item={adminItem} emphasized /> : null}
      </nav>
    </aside>
  );
}

export function MobileNavigation() {
  const { hasFeature, hasRole } = useAuth();
  const visiblePrimaryItems = primaryItems.filter((item) => {
    if (hasRole("sales")) return ["today", "schedule", "quotes", "sms", "import"].includes(item.key);
    if (item.key === "speedToLead") return hasRole("owner") && hasFeature("speed_to_lead");
    if (item.key === "dailyReview" || item.key === "murrayLab") return hasRole("owner", "gm");
    if (item.key === "import") return hasRole("owner", "gm", "sales");
    if (item.key === "sms") return hasRole("owner", "gm", "sales");
    return true;
  });
  const showAdmin = hasRole("owner", "gm");
  const items = showAdmin ? [...visiblePrimaryItems, adminItem] : visiblePrimaryItems;

  return (
    <nav className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white/95 px-2 pb-[calc(env(safe-area-inset-bottom)+0.35rem)] pt-2 shadow-[0_-8px_24px_rgba(15,23,42,0.12)] backdrop-blur md:hidden">
      <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
        {items.map((item) => (
          <MobileNavLink key={item.key} item={item} />
        ))}
      </div>
    </nav>
  );
}

function SidebarLink({ item, emphasized = false }: { item: SidebarItem; emphasized?: boolean }) {
  const matchRoute = useMatchRoute();
  const isActive = !item.external && Boolean(matchRoute({ to: item.href, fuzzy: item.href !== "/" }));
  const classes = cn(
    "flex h-11 min-w-fit items-center gap-3 rounded-md px-3 text-sm font-semibold transition-colors md:w-full",
    isActive
      ? "bg-[#1587c8] text-white shadow-[0_8px_20px_rgba(21,135,200,0.32)]"
      : emphasized
        ? "text-slate-200 hover:bg-white/10 hover:text-white"
        : "text-slate-300 hover:bg-white/10 hover:text-white",
  );

  if (item.external) {
    return (
      <a className={classes} href={item.href}>
        <SidebarIcon active={isActive} name={item.icon} />
        <span>{item.label}</span>
        <span className="ml-auto hidden text-[11px] text-slate-500 md:inline" aria-hidden="true">
          ↗
        </span>
      </a>
    );
  }

  return (
    <Link className={classes} to={item.href}>
      <SidebarIcon active={isActive} name={item.icon} />
      <span>{item.label}</span>
    </Link>
  );
}

function MobileNavLink({ item }: { item: SidebarItem }) {
  const matchRoute = useMatchRoute();
  const isActive = !item.external && Boolean(matchRoute({ to: item.href, fuzzy: item.href !== "/" }));
  const classes = cn(
    "flex min-w-0 flex-col items-center justify-center gap-1 rounded-md px-1 py-1.5 text-[10px] font-medium transition-colors",
    isActive ? "bg-[#1587c8] text-white" : "text-slate-500 hover:bg-slate-100 hover:text-slate-950",
  );

  return (
    <Link className={classes} to={item.href}>
      <span
        className={cn(
          "flex h-5 w-5 items-center justify-center rounded",
          isActive ? "bg-white/10 text-white" : "text-slate-500",
        )}
      >
        <Icon name={item.icon} />
      </span>
      <span className="max-w-full truncate">{item.mobileLabel || item.label}</span>
    </Link>
  );
}

function SidebarIcon({ active, name }: { active: boolean; name: SidebarIconName }) {
  return (
    <span
      className={cn(
        "flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] font-semibold",
        active ? "bg-white/20 text-white" : "bg-white/10 text-slate-300",
      )}
    >
      <Icon name={name} />
    </span>
  );
}

function Icon({ name }: { name: SidebarIconName }) {
  const common = {
    fill: "none",
    stroke: "currentColor",
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    strokeWidth: 2,
  };

  return (
    <svg aria-hidden="true" className="h-3.5 w-3.5" viewBox="0 0 24 24">
      {name === "today" ? (
        <>
          <path {...common} d="M8 2v4M16 2v4M4 9h16" />
          <rect {...common} x="4" y="4" width="16" height="18" rx="2" />
          <path {...common} d="m9 15 2 2 4-5" />
        </>
      ) : null}
      {name === "quotes" ? (
        <>
          <path {...common} d="M7 7h10M7 12h8M7 17h5" />
          <rect {...common} x="4" y="3" width="16" height="18" rx="2" />
        </>
      ) : null}
      {name === "review" ? (
        <>
          <path {...common} d="M9 11h6M9 15h4" />
          <path {...common} d="M8 3h8l3 3v15H5V3h3Z" />
          <path {...common} d="M16 3v4h4" />
          <path {...common} d="m8 19 1.5 1.5L13 17" />
        </>
      ) : null}
      {name === "brain" ? (
        <>
          <path {...common} d="M8 4a4 4 0 0 0-4 4v1.5A3.5 3.5 0 0 0 7.5 13H8v3a4 4 0 0 0 8 0v-3h.5A3.5 3.5 0 0 0 20 9.5V8a4 4 0 0 0-4-4" />
          <path {...common} d="M8 4a4 4 0 0 1 4 4v12M16 4a4 4 0 0 0-4 4" />
          <path {...common} d="M6 10h4M14 10h4M8 16h3M13 16h3" />
        </>
      ) : null}
      {name === "speed" ? (
        <>
          <path {...common} d="M4 13h7L8 21l12-12h-7l3-6L4 13Z" />
        </>
      ) : null}
      {name === "schedule" ? (
        <>
          <path {...common} d="M4 6h16M8 3v6M16 3v6" />
          <rect {...common} x="4" y="5" width="16" height="16" rx="2" />
          <path {...common} d="M8 14h3M14 14h2M8 18h2" />
        </>
      ) : null}
      {name === "import" ? (
        <>
          <path {...common} d="M12 3v12" />
          <path {...common} d="m8 11 4 4 4-4" />
          <path {...common} d="M4 19h16" />
        </>
      ) : null}
      {name === "sms" ? (
        <>
          <path {...common} d="M5 6h14v9H8l-3 3V6Z" />
          <path {...common} d="M8 10h8M8 13h5" />
        </>
      ) : null}
      {name === "reviews" ? (
        <>
          <path {...common} d="m12 3 2.7 5.5 6.1.9-4.4 4.3 1 6-5.4-2.8-5.4 2.8 1-6-4.4-4.3 6.1-.9L12 3Z" />
        </>
      ) : null}
      {name === "admin" ? (
        <>
          <circle {...common} cx="12" cy="12" r="3" />
          <path {...common} d="M19 12a7.4 7.4 0 0 0-.1-1l2-1.5-2-3.4-2.4 1a7 7 0 0 0-1.7-1L14.5 3h-5l-.4 3.1a7 7 0 0 0-1.7 1l-2.4-1-2 3.4 2 1.5a7.4 7.4 0 0 0 0 2l-2 1.5 2 3.4 2.4-1a7 7 0 0 0 1.7 1l.4 3.1h5l.4-3.1a7 7 0 0 0 1.7-1l2.4 1 2-3.4-2-1.5c.1-.3.1-.7.1-1Z" />
        </>
      ) : null}
    </svg>
  );
}
